import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { AccordionModule } from 'primeng/accordion';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';

import { Restriction, FileDto } from 'src/app/models/restriction';
import { FileService } from 'src/app/services/file.service';
import { DrivingRestrictionFileInfo } from 'src/app/models/file-type-driving-restriction-enum';

@Component({
  selector: 'app-restriction-details',
  templateUrl: './restriction-details.component.html',
  styleUrl: './restriction-details.component.scss',
  standalone: true,
  imports: [FormsModule, DialogModule, DatePipe, CardModule, AccordionModule],
})
export class RestrictionDetailsComponent implements OnInit, OnChanges {
  @Input() restricion: Restriction | null = null;
  @Input() showFpc = true;

  // Filenames split by type for display
  loadingFiles = false;
  fileNamesDocuments: string[] = [];
  fileNamesImages: string[] = [];

  // Map file name -> fileUid so we can request pre-signed URLs on click
  private nameToUid = new Map<string, string>();

  // Image preview dialog state
  showImagePreview = false;
  previewSrc: string | null = null;
  previewTitle = '';
  loadingPreview = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    if (this.restricion?.fileUids?.length) {
      this.loadFileInfos(this.restricion.fileUids);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['restricion'] && this.restricion) {
      const uids = this.restricion.fileUids ?? [];
      this.loadFileInfos(uids);
    }
  }

  // ----- UI helpers used in template -----

  vehicleNames(): string {
    return this.restricion?.vehicles?.map(v => v.name).join(', ') ?? '';
  }

  fpcLabel(fpcId: string, name: string): string {
    return `${fpcId.replace(/^FPC/i, '')} - ${name}`;
  }

  // Click on a document name -> download
  downloadByName(name: string): void {
    const uid = this.nameToUid.get(name);
    if (!uid) return;

    this.fileService.generatePresignedDownloadUrl(uid).pipe(take(1)).subscribe({
      next: (url) => this.fileService.downloadFile(url, name),
      error: () => {
        // optional: show a toast
      }
    });
  }

  // Click on an image name -> preview in modal
  previewImageByName(name: string): void {
    const uid = this.nameToUid.get(name);
    if (!uid) return;

    this.previewTitle = name;
    this.previewSrc = null;
    this.loadingPreview = true;
    this.showImagePreview = true;

    this.fileService.generatePresignedDownloadUrl(uid).pipe(
      take(1),
      finalize(() => (this.loadingPreview = false))
    ).subscribe({
      next: (url) => {
        this.previewSrc = url; // direct S3 URL works as <img src>
        this.cdr.detectChanges();
      },
      error: () => {
        this.previewSrc = null;
        // optional: show a toast
      }
    });
  }

  closeImagePreview(): void {
    this.showImagePreview = false;
    this.previewSrc = null;
    this.previewTitle = '';
  }

  private loadFileInfos(fileDtos: FileDto[]): void {
    this.fileNamesDocuments = [];
    this.fileNamesImages = [];
    this.nameToUid.clear();

    const cleanFds: FileDto[] = (fileDtos ?? []).filter(
      (fd): fd is FileDto => !!fd?.fileUid && fd.fileUid.trim().length > 0
    );

    if (cleanFds.length === 0) {
      this.cdr.detectChanges();
      return;
    }

    this.loadingFiles = true;

    // Normalizer to tolerate different casing from backends
    const normalize = (i: any) => ({
      uid:  i?.FileUid  ?? i?.fileUid ?? i?.uid ?? '',
      name: i?.FileName ?? i?.fileName ?? i?.name ?? ''
    });

    forkJoin(
      cleanFds.map(fd =>
        this.fileService.retrieveFileInfo(fd.fileUid).pipe(
          catchError(() =>
            of<DrivingRestrictionFileInfo>({ FileUid: fd.fileUid, FileName: '(unknown)' })
          )
        )
      )
    )
    .pipe(finalize(() => (this.loadingFiles = false)))
    .subscribe((rawInfos) => {
      const infos = rawInfos.map(normalize);
      const images: string[] = [];
      const docs: string[] = [];

      for (const info of infos) {
        const name = info.name || '(unknown)';
        const uid  = info.uid  || '';
        if (name && uid) this.nameToUid.set(name, uid);

        (this.isImage(name) ? images : docs).push(name);
      }

      this.fileNamesImages = images;
      this.fileNamesDocuments = docs;
      this.cdr.detectChanges();
    });
  }

  private isImage(fileName?: string | null): boolean {
    const ext = this.getExt(fileName);
    return ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.tif', '.heic', '.heif']
      .includes(ext);
  }

  private getExt(fileName?: string | null): string {
    if (!fileName) return '';
    const i = fileName.lastIndexOf('.');
    return i >= 0 ? fileName.substring(i).toLowerCase() : '';
  }
}
